import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Switch,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import {
  CollectionsBookmark as SeriesIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Article as ArticleCountIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import * as seriesService from '../services/articleSeriesService';
import { ConfirmDialog } from '../components/common';

const ArticleSeriesManagement: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // Data
  const [seriesList, setSeriesList] = useState<seriesService.ArticleSeries[]>([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSeries, setEditingSeries] = useState<seriesService.ArticleSeries | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingSeries, setDeletingSeries] = useState<seriesService.ArticleSeries | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formCoverImage, setFormCoverImage] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);

  // Load series
  const loadSeries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await seriesService.getSeries(page + 1, rowsPerPage, searchQuery || undefined);
      setSeriesList(response.docs || []);
      setTotalDocs(response.totalDocs || 0);
    } catch (err: any) {
      setError(err.message || 'خطا در بارگذاری مجموعه‌ها');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery]);

  useEffect(() => {
    loadSeries();
  }, [loadSeries]);

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Open create dialog
  const handleCreate = () => {
    setEditingSeries(null);
    setFormTitle('');
    setFormDescription('');
    setFormCoverImage('');
    setFormIsActive(true);
    setDialogOpen(true);
  };

  // Open edit dialog
  const handleEdit = (series: seriesService.ArticleSeries) => {
    setEditingSeries(series);
    setFormTitle(series.title);
    setFormDescription(series.description || '');
    setFormCoverImage(series.coverImage || '');
    setFormIsActive(series.isActive);
    setDialogOpen(true);
  };

  // Submit form
  const handleSubmit = async () => {
    if (!formTitle.trim()) {
      enqueueSnackbar('عنوان مجموعه الزامی است', { variant: 'warning' });
      return;
    }
    if (formTitle.trim().length < 3) {
      enqueueSnackbar('عنوان مجموعه باید حداقل ۳ کاراکتر باشد', { variant: 'warning' });
      return;
    }

    setSubmitting(true);
    try {
      if (editingSeries) {
        await seriesService.updateSeries(editingSeries._id, {
          title: formTitle.trim(),
          description: formDescription.trim() || undefined,
          coverImage: formCoverImage.trim() || undefined,
          isActive: formIsActive,
        });
        enqueueSnackbar('مجموعه با موفقیت ویرایش شد', { variant: 'success' });
      } else {
        await seriesService.createSeries({
          title: formTitle.trim(),
          description: formDescription.trim() || undefined,
          coverImage: formCoverImage.trim() || undefined,
        });
        enqueueSnackbar('مجموعه با موفقیت ایجاد شد', { variant: 'success' });
      }
      setDialogOpen(false);
      loadSeries();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'خطا در ذخیره مجموعه';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    if (!deletingSeries) return;
    setSubmitting(true);
    try {
      await seriesService.deleteSeries(deletingSeries._id);
      enqueueSnackbar('مجموعه با موفقیت حذف شد', { variant: 'success' });
      setDeleteDialogOpen(false);
      setDeletingSeries(null);
      loadSeries();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'خطا در حذف مجموعه';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SeriesIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>مدیریت مجموعه‌ها</Typography>
            <Typography variant="body2" color="text.secondary">ایجاد و مدیریت مجموعه‌های مقالات</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadSeries} disabled={loading}>
            بارگذاری مجدد
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
            مجموعه جدید
          </Button>
        </Box>
      </Box>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="جستجوی مجموعه..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>عنوان</TableCell>
                <TableCell>توضیحات</TableCell>
                <TableCell align="center">تعداد مقالات</TableCell>
                <TableCell align="center">وضعیت</TableCell>
                <TableCell align="center">ایجادکننده</TableCell>
                <TableCell align="center">عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : seriesList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">مجموعه‌ای یافت نشد</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                seriesList.map((series) => (
                  <TableRow key={series._id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {series.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {series.slug}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{
                        maxWidth: 250,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {series.description || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={<ArticleCountIcon sx={{ fontSize: 16 }} />}
                        label={series.articleCount}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={series.isActive ? 'فعال' : 'غیرفعال'}
                        color={series.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption">
                        {series.createdBy?.name || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="ویرایش">
                        <IconButton size="small" onClick={() => handleEdit(series)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="حذف">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setDeletingSeries(series);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalDocs}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage="تعداد در صفحه:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} از ${count}`}
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => !submitting && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ direction: 'rtl' }}>
          {editingSeries ? 'ویرایش مجموعه' : 'ایجاد مجموعه جدید'}
        </DialogTitle>
        <DialogContent sx={{ direction: 'rtl' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="عنوان مجموعه *"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              placeholder="عنوان مجموعه را وارد کنید"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              fullWidth
              label="توضیحات"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder="توضیح کوتاه (اختیاری)"
              multiline
              rows={3}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              fullWidth
              label="آدرس تصویر کاور"
              value={formCoverImage}
              onChange={(e) => setFormCoverImage(e.target.value)}
              placeholder="https://... (اختیاری)"
              sx={{ direction: 'ltr', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            {editingSeries && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formIsActive}
                    onChange={(e) => setFormIsActive(e.target.checked)}
                    color="primary"
                  />
                }
                label="فعال"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ direction: 'rtl', px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting} color="inherit">
            انصراف
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {editingSeries ? 'ذخیره تغییرات' : 'ایجاد'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="حذف مجموعه"
        message={`آیا از حذف مجموعه «${deletingSeries?.title}» مطمئن هستید؟`}
        confirmText="حذف"
        cancelText="انصراف"
        confirmColor="error"
        loading={submitting}
        onCancel={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
      >
        {(deletingSeries?.articleCount ?? 0) > 0 && (
          <Alert severity="warning">
            این مجموعه {deletingSeries?.articleCount} مقاله دارد. ابتدا مقالات را از مجموعه خارج کنید.
          </Alert>
        )}
      </ConfirmDialog>
    </Container>
  );
};

export default ArticleSeriesManagement;
